import React, { MouseEvent } from "react";
import { useRecoilValue } from "recoil";
import styled from "styled-components";
import {
  breakpoints,
  Button,
  colors,
  fontSizes,
  fontWeights,
  Link,
  Size,
  Text,
  useWindowDimensions,
} from "@trailhead/asurion-ui";
import { trackEvent } from "@trailhead/analytics";
import { useChatClient } from "@trailhead/chat";
import { componentRefListState } from "@trailhead/global-device-context";

import Priscilla from "../assets/Priscilla.png";
import Ellipse from "../assets/Ellipse.svg";
import Star from "../assets/Star.svg";
import ElmerAvatar from "../assets/ElmerAvatar.jpeg";
import PriscillaAvatar from "../assets/PriscillaAvatar.png";
import Sparks from "../assets/Sparks.svg";
import { ChatDemo } from "./ChatDemo";

const Container = styled.div`
  display: flex;
  justify-content: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  padding: 0 0.75rem;
  content: "";
  background-color: ${colors.neutralBrighter};
  box-shadow: 0 0 0 100vmax ${colors.neutralBrighter};
  clip-path: inset(0 -100vmax);

  ${breakpoints.lg} {
    padding: 0 1.875rem;
  }
`;

const Content = styled.div`
  max-width: 27.5rem;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 2.125rem;

  ${breakpoints.lg} {
    width: 100%;
    max-width: unset;
    flex-direction: row;
    justify-content: center;
  }
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 1.5rem;

  ${breakpoints.lg} {
    justify-content: center;
    align-items: start;
    margin: 4.875rem 0;
  }
`;

const Title = styled(Text)`
  margin: 0;
  font-weight: ${fontWeights.base};
  font-size: ${fontSizes[6]};
  line-height: 1.2;
  text-align: center;

  ${breakpoints.lg} {
    font-size: ${fontSizes[8]};
    text-align: left;
  }
`;

const Details = styled(Text)`
  margin: 1.5rem 0.5rem 0;
  font-weight: ${fontWeights.base};
  font-size: ${fontSizes[3]};
  line-height: 1.2;
  text-align: center;

  ${breakpoints.lg} {
    text-align: left;
    font-size: ${fontSizes[4]};
    margin-left: 0;
  }
`;

const CTAButton = styled(Button)`
  &&& {
    width: 14.375rem;
    margin-top: 2.25rem;
  }
`;

const ViewAllSupport = styled(Link)`
  margin-top: 1.25rem;
`;

const ImageContent = styled.div`
  display: grid;
  position: relative;
  width: 100%;
  max-width: 24.375rem;
  grid-template-columns: auto 1fr;
  grid-template-rows: 1fr auto;

  ${breakpoints.lg} {
    align-self: flex-end;
    max-width: 35rem;
  }
`;

const Model = styled.img`
  display: block;
  align-self: flex-end;
  width: 9.5rem;
  grid-row: 1 / -1;

  ${breakpoints.lg} {
    width: 13.125rem;
  }
`;

const EllipseShape = styled.img`
  display: block;
  z-index: -1;
  position: absolute;
  bottom: 0;
  width: 18.125rem;

  ${breakpoints.lg} {
    width: 24.875rem;
  }
`;

const ExpertLabel = styled.div`
  margin: 1rem 0;
  grid-column: 2;
`;

const ExpertName = styled(Text)`
  color: ${colors.white};
  font-size: ${fontSizes[0]};

  ${breakpoints.lg} {
    font-size: 0.875rem;
  }
`;

const ExpertRating = styled.div`
  display: flex;
  align-items: center;
  column-gap: 0.3125rem;
`;

const StarContainer = styled.div`
  position: relative;
`;

const StarShape = styled.img`
  display: block;
  width: 0.625rem;

  ${breakpoints.lg} {
    width: 0.8125rem;
    font-size: ${fontSizes[3]};
  }
`;

const StarBg = styled(StarShape)`
  position: absolute;
  inset: 0;
  filter: brightness(0.5);
`;

const LastStar = styled(StarShape)`
  clip-path: polygon(100% 0, 100% 0%, 55% 100%, 0 100%, 0 0);
`;

const RatingNumber = styled(Text)`
  position: relative;
  bottom: -0.0625rem;
  color: ${colors.white};
  font-weight: ${fontWeights.heavy};
  font-size: ${fontSizes[0]};
  line-height: 1;

  ${breakpoints.lg} {
    font-size: 0.625rem;
  }
`;

const ChatDemos = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 0.25rem;

  ${breakpoints.lg} {
    row-gap: 0.5rem;
  }
`;

const ChatDemoSender = styled.div`
  display: flex;
  align-items: flex-start;
  margin-left: auto;
  position: relative;

  ${breakpoints.lg} {
    top: 0.75rem;
  }
`;

const SparksShape = styled.img`
  position: relative;
  top: 0.5rem;
  left: 0.625rem;
  width: 2rem;
  margin-left: -0.625rem;

  ${breakpoints.lg} {
    top: 1rem;
    width: 2.5rem;
  }
`;

type Props = {
  callEnabled?: boolean;
};

export const VerizonQuickSupportBanner = ({ callEnabled = false }: Props) => {
  const chatClient = useChatClient();
  const dimension = useWindowDimensions();
  const isLargeScreen = dimension.width >= Size.LG;

  const phoneNumber = { value: "+18663147861", label: "866-314-7861" };
  const callButtonText = `Call: ${phoneNumber.label}`;
  const componentRefList = useRecoilValue(componentRefListState);

  const handleCallButtonClick = () => {
    window.open(`tel:${phoneNumber.value}`, "_self");
    trackEvent("Pathfinder_HeroBanner_CallBtn", {
      buttonText: callButtonText,
    });
  };

  const handleChatBtnClick = () => {
    trackEvent("Pathfinder_HeroBanner_ChatBtn");
    chatClient.showMessaging();
    chatClient.openMessagingOverlay();
  };

  const handleViewOptionsClick = (event: MouseEvent, elementId: string) => {
    event.preventDefault();
    trackEvent("Pathfinder_HeroBanner_ViewSupportOptions");
    const targetElement = componentRefList[elementId];
    targetElement?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  return (
    <Container>
      <Content>
        <TextContent>
          <Title as="h1">
            <strong>Quick support</strong> for <br />
            setting up your device!
          </Title>
          <Details as="h3">Activate, optimize, and manage your device.</Details>
          {callEnabled ? (
            <CTAButton
              size={isLargeScreen ? "medium" : "small"}
              iconSrc="Phone"
              onClick={handleCallButtonClick}
            >
              {callButtonText}
            </CTAButton>
          ) : (
            <CTAButton
              size={isLargeScreen ? "medium" : "small"}
              iconSrc="ChatBubbleCircle"
              onClick={handleChatBtnClick}
            >
              Chat with an expert
            </CTAButton>
          )}
          {/* Will remain a constant for now. Will add a fancier way to implement local navigation. */}
          <ViewAllSupport
            href="#self-guided-support-cards-section"
            onClick={event =>
              handleViewOptionsClick(event, "self-guided-support-cards-section")
            }
          >
            View all support options
          </ViewAllSupport>
        </TextContent>
        <ImageContent>
          <Model src={Priscilla} />
          <EllipseShape src={Ellipse} />
          <ChatDemos>
            <ChatDemo
              avatar={ElmerAvatar}
              message="“I need help activating my phone”"
            />
            <ChatDemoSender>
              <SparksShape src={Sparks} alt="sparks" />
              <ChatDemo
                avatar={PriscillaAvatar}
                message="Ok, I can help you with that! What type of device do you have?"
                maxLine={2}
              />
            </ChatDemoSender>
            <ChatDemo
              avatar={ElmerAvatar}
              message="“Great! I have an iPhone14.”"
            />
          </ChatDemos>
          <ExpertLabel>
            <ExpertName>
              Priscilla, <strong>Tech Coach</strong>
            </ExpertName>
            <ExpertRating>
              {[1, 2, 3, 4, 5].map(starPosition => {
                if (starPosition === 5)
                  return (
                    <StarContainer key={starPosition}>
                      <StarBg src={Star} />
                      <LastStar src={Star} />
                    </StarContainer>
                  );
                return <StarShape src={Star} key={starPosition} />;
              })}

              <RatingNumber>4.97</RatingNumber>
            </ExpertRating>
          </ExpertLabel>
        </ImageContent>
      </Content>
    </Container>
  );
};
